/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { html, LitElement } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map.js';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { convertToRem } from '../../utils/pixel-to-rem-convertor.js';
import { inputTextStyles } from './sh-input-text-styles.js';

/**
 * @prop {String} label - Defines the label of the input. If the value is empty, it will be shown as a placeholder.
 * @prop {String} value - Represents the current value of the input. Can be used for data binding and changes upon user input.
 * @prop {Boolean} textarea - If set to `true`, the input will behave like a textarea, which means will allow multiple rows of text and vertical resizing .
 * @prop {Boolean} resizable - If set to `true`, the resize will be enabled for textarea.
 * @prop {String} rows - Only if textarea is true, this will define the amount of rows shown by default.
 * @prop {Boolean} mandatory - If set to `true`, the input text displays styles for mandatory input.
 * @prop {Boolean} readonly - If set to `true`, the field will be set to readonly and thus won't allow user input.
 * @prop {Boolean} disabled - If set to `true`, input won't be allowed and disabled styles will be applied.
 * @prop {String} maxlength - Defines the maximum length allowed for the text (in number of letters).
 * @prop {Boolean} error - If set to `true`, the input text displays styles for invalid input.
 * @prop {Boolean} success - If set to `true`, the input text displays styles for valid input.
 * @prop {Boolean} safety - If set to `true`, the input text displays styles for safety-relevant input (yellow outline).
 * @prop {String} hint - Displays a message when error property is set to true and when error-message is not an empty string.
 * @prop {String} errorMessage - Displays a message when error property is set to true and when error-message is not undefined.
 * @attr {String} error-message - Displays a message when error property is set to true and when error-message is not undefined.
 * @prop {Boolean} condensed - If input text is inside a table, then the condensed format of input text is used to display information. This feature cannot be overriden.
 * @prop {Boolean} autofocus - If set to `true`, the first sh-input-text having this attribute as true, will have focus on page load. Behaviour similar to autofocus attribute of HTML5 text input
 * @prop {Boolean} noBorder - If set to `true`, the bottom border will not be displayed.
 * @prop {Boolean} noClear - If set to `true`, no clear button is displayed.
 * @prop {Boolean} _notifyInputClick - *hide If set to true, fires input-clicked event on click of input.
 * @prop {Boolean} _notifyClearClick - *hide If set to true, fires clear-clicked event on click of clear icon.
 * @prop {Boolean} _inputReadonly - *hide If set to true, will set the inner input to readonly.
 * Use this when reusing sh-input-text in other components.
 * @prop {Boolean} _showOutline - *hide Set to true to show the outline even if focus is not there in the input.
 * Use this to link to the active property of parent component when sh-input-text is reused in other components.
 * @attr {Boolean} show-outline - *hide Set to true to show the outline even if focus is not there in the input.
 * Use this to link to the active property of parent component when sh-input-text is reused in other components.
 * @prop {String} role - *hide
 * @prop {Number} _iconWrapperWidth - *hide The width of icon-wrapper. Used to compute the width of the label and the
 * padding-right of the input. Thus removing a lot of unwanted css rules.
 * @attr {Boolean} no-border - If set to true, the bottom border will not be displayed.
 * @prop {String} type - If set to password, the value will be shown as dots.
 * @slot icon - Takes sh-icon as children. Will render icon in the sh-input-text.
 * @fires input-clicked - *hide Fired when input is clicked (if _notifyInputClick is set to true)
 * @fires clear-clicked - *hide Fired when clear icon is clicked (if _notifyClearClick is set to true)
 */
export class SHInputText extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: String, reflect: true },
      hint: { type: String, reflect: true },
      type: { type: String, reflect: true },
      textarea: { type: Boolean, reflect: true },
      rows: { type: String, reflect: true },
      maxlength: { type: String, reflect: true },
      readonly: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      role: { type: String, reflect: true },
      mandatory: { type: Boolean, reflect: true },
      error: { type: Boolean, reflect: true },
      success: { type: Boolean, reflect: true },
      safety: { type: Boolean, reflect: true },
      errorMessage: { type: String, reflect: true, attribute: 'error-message' },
      condensed: { type: Boolean, reflect: true },
      resizable: { type: Boolean, reflect: true },
      noClear: { type: Boolean, reflect: true, attribute: 'no-clear' },
      autofocus: { type: Boolean, reflect: true },
      noBorder: { type: Boolean, reflect: true, attribute: 'no-border' },
      _iconslot: { type: Boolean, reflect: true },
      _notifyInputClick: { type: Boolean },
      _notifyClearClick: { type: Boolean },
      _inputReadonly: { type: Boolean },
      _showOutline: { type: Boolean, reflect: true, attribute: 'show-outline' },
      _iconWrapperWidth: { type: Number },
    };
  }
  static get styles() {
    return [inputTextStyles, sharedStyles];
  }
  render() {
    return html`
      <div id="shinputDiv" class="content-wrapper">
        ${!this.textarea
          ? html`
              <input
                type="${this.type}"
                @input="${(e) => (this.value = e.target.value)}"
                ?readonly="${this.readonly || this._inputReadonly}"
                .value="${!this.value ? '' : this.value}"
                maxlength="${this.maxlength}"
                id="input"
                ?disabled="${this.disabled}"
                ?autofocus="${this.autofocus}"
                @click="${() => {
                  if (this._notifyInputClick) {
                    dispatchCustomEvent(this, 'input-clicked', null, false, false);
                  }
                }}"
                style="${styleMap(this._getInputPaddingRight())}"
              />
            `
          : html`
              <textarea
                class="textarea"
                .value="${!this.value ? '' : this.value}"
                @input="${(e) => (this.value = e.target.value)}"
                maxlength="${this.maxlength}"
                rows="${this.rows}"
                ?readonly="${this.readonly}"
                ?disabled="${this.disabled}"
                ?autofocus="${this.autofocus}"
              >
${this.value}</textarea
              >
            `}

        <div class="input-text-label" style="${styleMap(this._getLabelWidth())}">
          ${this.label ? html` <div class="label-wrapper">${this.label}</div> ` : ''}
          ${this.mandatory ? html` <span id="mandatory"> *</span> ` : ''}
        </div>
      </div>
      <div class="icon-wrapper">
        ${this.value && !this.noClear && !this.disabled && !this.readonly
          ? html`
              <sh-icon
                size="s"
                button
                icon="cancel"
                id="clear"
                tabindex="${this.value > 0 ? '' : '0'}"
                @click="${(e) => {
                  this._clearField(e);
                  if (this._notifyClearClick) {
                    dispatchCustomEvent(this, 'clear-clicked', null, false, false);
                  }
                }}"
                @focusout="${() => {
                  this._enableClear = false;
                }}"
              ></sh-icon>
            `
          : ''}
        ${this.success || this.error
          ? html`
              <sh-icon
                icon="${this.error ? 'error' : 'success'}"
                size="s"
                ?disabled="${this.disabled}"
              ></sh-icon>
            `
          : ''}
        <slot name="icon" id="icon" @slotchange="${() => this._iconSlotObserver()}"></slot>
      </div>
      ${this.hint && !this.errorMessage
        ? html`
            <sh-text size="body-2" class="helper-text hint" color="secondary">${this.hint}</sh-text>
          `
        : ''}
      ${this.hint && this.errorMessage && !this.error
        ? html`
            <sh-text size="body-2" class="helper-text hint" color="secondary">${this.hint}</sh-text>
          `
        : ''}
      ${this.error && this.errorMessage
        ? html`
            <sh-text size="body-2" class="helper-text error-message" color="secondary"
              >${this.errorMessage}</sh-text
            >
          `
        : ''}
    `;
  }
  constructor() {
    super();
    this.label = 'label';
    this.type = 'text';
    this.value = '';
    this._iconWrapperWidth = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    /** @ignore */
    this.textareaLabelClick = this._handleTextareaLabelClick.bind(this);
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('no-hovermq');
    }
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'textbox');
    }
    if (this.textarea) {
      document.body.addEventListener('click', this.textareaLabelClick);
    }
    this._addTabindex();
    this._resizeObserverListener = this._resizeObserverCallback.bind(this);
    this._resizeObserver = new ResizeObserver(this._resizeObserverListener);
  }
  disconnectedCallback() {
    document.body.removeEventListener('click', this.textareaLabelClick);
    this._resizeObserver.disconnect();
    super.disconnectedCallback();
  }
  firstUpdated() {
    super.firstUpdated();
    this._resizeObserver.observe(this.shadowRoot.querySelector('.icon-wrapper'));
  }
  updated(changedProperties) {
    if (changedProperties.has('value')) {
      this._handleValueChanged();
    }

    if (changedProperties.has('resizable') && this.textarea) {
      this._handleResizableChanged();
    }
    if (this.textarea && !this.rows) {
      this.rows = 3;
    }

    const listOfProperties = [
      'label',
      'value',
      'textarea',
      'resizable',
      'type',
      'condensed',
      'error',
      'readonly',
      'disabled',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  focus() {
    if (this.textarea) {
      this.shadowRoot.querySelector('textarea').focus();
    } else {
      this.shadowRoot.querySelector('#input').focus();
    }
  }
  defocus() {
    this.shadowRoot.querySelector('#input').blur();
  }

  select() {
    if (this.textarea) {
      this.shadowRoot.querySelector('textarea').select();
    } else {
      this.shadowRoot.querySelector('#input').select();
    }
  }

  _addTabindex() {
    const iconLength = this.querySelectorAll('sh-icon').length;
    if (!this.disabled && iconLength > 0) {
      for (let i = 0; i < iconLength; i++) {
        this.querySelectorAll('sh-icon')[i].setAttribute('tabindex', '0');
      }
    }
  }

  _handleTextareaLabelClick(e) {
    if (e.target === this && this.textarea) {
      this.shadowRoot.querySelector('textarea').focus();
    }
  }

  _handleValueChanged() {
    if (this.value) {
      this.classList.remove('empty');
    } else {
      this.classList.add('empty');
    }
    if (this.textarea) {
      const textarea = this.shadowRoot.querySelector('textarea');
      if (textarea.scrollHeight > textarea.clientHeight) {
        textarea.title = this.value;
      } else {
        textarea.title = '';
      }
    }
  }

  _handleResizableChanged() {
    if (this.textarea && this.resizable) {
      this.shadowRoot.querySelector('textarea').style.resize = 'vertical';
    } else {
      this.shadowRoot.querySelector('textarea').style.resize = 'none';
    }
  }

  _iconSlotObserver() {
    const iconNodeLength = this.shadowRoot.querySelector('#icon').assignedNodes({
      flatten: true,
    }).length;
    if (iconNodeLength > 0) {
      /**@ignore */
      this._iconslot = true;
    } else {
      this._iconslot = false;
    }
  }
  _clearField() {
    this.value = '';
    if (this.textarea) {
      this.shadowRoot.querySelector('textarea').focus();
    } else {
      this.shadowRoot.querySelector('#input').focus();
    }
  }
  _getLabelWidth() {
    return {
      // an extra 0.5rem is added to the input padding right to compensate for
      // the css: left:0.5rem given to the label;
      width: `calc(100% - ${this._getTotalInputPaddingRight() + 0.5}rem)`,
    };
  }
  _getInputPaddingRight() {
    return {
      'padding-right': `${this._getTotalInputPaddingRight()}rem`,
    };
  }
  /**
   *
   * @returns total padding right of the input type=text based on the width of the
   * icon-wrapper
   */
  _getTotalInputPaddingRight() {
    // if no icons are present, then input padding-right is 8px or 0.5rem;
    // if icons are present, then input padding-right is icon-wrapper-width + 8px(0.5rem) on both sides of icon-wrapper = iWW(rem) + (0.5*2)(rem)
    let totalInputPaddingRight;
    if (this._iconWrapperWidth === 0) {
      totalInputPaddingRight = 0.5;
    } else {
      totalInputPaddingRight = 0.5 + convertToRem(this._iconWrapperWidth) + 0.5;
    }
    return totalInputPaddingRight;
  }
  _resizeObserverCallback(entry) {
    this._iconWrapperWidth = entry[0].target.getBoundingClientRect().width;
  }
}
customElements.define('sh-input-text', SHInputText);
